export default class Subscription {
  id!: string
  plan!: string
  // Audit field: the plan the Notch trial ran on, preserved by the BE after soft lock swaps
  // `plan` to TRIAL_EXPIRED.
  trialPlanCode?: string | null
  seats!: number
  billingCycle!: BillingCycle
  isCustomPricing!: boolean
  freeTrialStart!: string | null
  freeTrialEnd!: string | null
  trialLockedAt!: string | null
  trialOrigin!: boolean
  isTrialActive!: boolean
  isTrialLocked!: boolean
  daysRemaining!: number
  warningLevel!: number | null
  permissions!: string[]
  /** Internal seats the effective plan allows (BASIC = 3); null/undefined means unlimited. */
  maxUsers?: number | null
  // NS-205: raw BE lifecycle state. `isTrialActive`/`isTrialLocked` above are the derived booleans
  // FE should branch on; these are kept for parity/debugging, not as a second source of truth.
  lifecycleState?: LifecycleState
  accessState?: AccessState
  /** Merged from entitlements when the BE sends `staticFirstRender`. */
  staticFirstRender?: boolean | null
}

/**
 * NS-205 entitlement payload of `GET /subscriptions/entitlements`.
 *
 * `GET /teams/current` serializes the Subscription *entity*, which carries none of these fields:
 * the BE computes them (after soft-lock enforcement) and serves them only from this endpoint, so
 * the two payloads have to be merged client-side. See `applyEntitlements` in TeamStore.
 */
export type SubscriptionEntitlements = {
  lifecycleState: LifecycleState
  accessState: AccessState
  isTrialActive: boolean
  isTrialLocked: boolean
  trialPlanCode: string | null
  /** The *effective* catalog plan. Becomes TRIAL_EXPIRED on soft lock; the entity's `plan` never moves. */
  planCode: string | null
  permissions: string[]
  /** Seats the effective plan allows; null means unlimited. */
  maxUsers: number | null
  daysRemaining: number | null
  warningLevel: number | null
  /** When present, BE rollout for `static_first_render`. Omitted until the field ships. */
  staticFirstRender?: boolean | null
}

export enum LifecycleState {
  NONE = 'NONE',
  NOTCH_TRIAL = 'NOTCH_TRIAL',
  PAID = 'PAID'
}

export enum AccessState {
  NORMAL = 'NORMAL',
  SOFT_LOCKED = 'SOFT_LOCKED'
}

export enum SubscriptionPlan {
  FREE = 'FREE',
  START = 'START',
  GROWTH = 'GROWTH',
  BASIC = 'BASIC',
  BUSINESS = 'BUSINESS',
  TRIAL_EXPIRED = 'TRIAL_EXPIRED',
  /**
   * Legacy plan the BE still has on the enum (`Subscription.kt:127`) and can serve on an old
   * subscription row. It has no catalog entry of its own and folds into GROWTH's permission set
   * (`Plan.kt:66-76`), so it is legacy-paid for cohort purposes — never a Notch-trial team.
   */
  SCALE = 'SCALE'
}

export enum BillingCycle {
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY'
}

export enum PlanTier {
  TIER1 = 'TIER1',
  TIER2 = 'TIER2',
  TIER3 = 'TIER3',
  NOTCH_TIER1_V2 = 'NOTCH_TIER1_V2',
  NOTCH_TIER2_V2 = 'NOTCH_TIER2_V2',
  NOTCH_TIER3_V2 = 'NOTCH_TIER3_V2',
  NOTCH_TIER4_V2 = 'NOTCH_TIER4_V2'
}
